import { OneTrustCategory } from '@square/onetrust-compliant-access';
import { getDeveloperApi } from '@squareup/dex-data-shared-developer-api';
import { useLocalStorage } from '@squareup/dex-ui-hooks';
import { ApiExplorerFeedbackContextProvider } from '@squareup/dex-ui-shared-api-explorer-providers';
import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';

interface Props {}

interface EndpointCountStore {
  [endpoint: string]: {
    count: number;
    lastResponse?: string;
  };
}
interface UserEndpointCountStore {
  [userId: string]: EndpointCountStore;
}

const userEndpointCountStorageKey = 'userEndpointCountStore';
const prevWeekDate = new Date(new Date().setDate(new Date().getDate() - 7));

/**
 * Helper function to compare the date with the date a week ago.
 * @param dateInString Date ISO string
 * @returns boolean whether the date is older than a week. True if undefined.
 */
function isDateOlderThanAWeek(dateInString: string | undefined) {
  if (!dateInString) {
    return true;
  }
  let moreThanWeekOld = true;
  const date = new Date(dateInString);
  if (date > prevWeekDate) {
    moreThanWeekOld = false;
  }
  return moreThanWeekOld;
}

const ApiExplorerFeedbackContainer: FC<PropsWithChildren<Props>> = ({
  children,
}) => {
  const [userEndpointCountStore, setUserEndpointCountStore] = useLocalStorage<
    UserEndpointCountStore | undefined
  >({
    key: userEndpointCountStorageKey,
    initialValue: undefined,
    category: OneTrustCategory.FUNCTIONAL,
  });

  const { data: userData } = getDeveloperApi().useGetUserInfoQuery();
  const userId = userData?.merchant?.id ?? 'defaultUser';

  const [showFeedbackPrompt, setShowFeedbackPrompt] = useState(false);

  const setShowPromptOnIncrement = useCallback(
    /**
     * Check if the prompt should be shown for an endpoint.
     * @param userEndpointData optional userEndpointData to check prompt conditions for.
     * @returns boolean whether the prompt should be shown for the given endpointId.
     */
    (userEndpointData?: EndpointCountStore[keyof EndpointCountStore]) => {
      if (
        !userEndpointData ||
        userEndpointData.count < 3 ||
        !isDateOlderThanAWeek(userEndpointData.lastResponse)
      ) {
        setShowFeedbackPrompt(false);
        return;
      }
      setShowFeedbackPrompt(true);
    },
    [setShowFeedbackPrompt]
  );

  const incrementUserEndpointCount = useCallback(
    /**
     * Increment the count for a given endpointId. This is intended to be used when the user
     * runs a request for an endpoint.
     * @param endpointId endpoint to increment count for.
     */
    (endpointId: string) => {
      const newStore: UserEndpointCountStore = {
        ...userEndpointCountStore,
        [userId]: {
          ...userEndpointCountStore?.[userId],
          [endpointId]: {
            ...userEndpointCountStore?.[userId]?.[endpointId],
            count:
              (userEndpointCountStore?.[userId]?.[endpointId]?.count ?? 0) + 1,
          },
        },
      };
      setUserEndpointCountStore(newStore);
      setShowPromptOnIncrement(newStore?.[userId]?.[endpointId]);
    },
    [
      setShowPromptOnIncrement,
      setUserEndpointCountStore,
      userEndpointCountStore,
      userId,
    ]
  );

  const resetUserEndpointCount = useCallback(
    /**
     * Reset the count for a given endpointId. This should be used when the user submits feedback.
     * @param endpointId endpoint to reset.
     * @param lastResponse Date string in ISO format (new Date().toISOString()).
     */
    (endpointId: string, lastResponse?: string) => {
      setUserEndpointCountStore({
        ...userEndpointCountStore,
        [userId]: {
          ...userEndpointCountStore?.[userId],
          [endpointId]: {
            ...userEndpointCountStore?.[userId]?.[endpointId],
            count: 0,
            ...(lastResponse && { lastResponse }),
          },
        },
      });
      setShowFeedbackPrompt(false);
    },
    [setUserEndpointCountStore, userEndpointCountStore, userId]
  );

  const memoizedContext = useMemo(() => {
    return {
      showFeedbackPrompt,
      incrementUserEndpointCount,
      resetUserEndpointCount,
    };
  }, [showFeedbackPrompt, incrementUserEndpointCount, resetUserEndpointCount]);

  return (
    <ApiExplorerFeedbackContextProvider value={memoizedContext}>
      {children}
    </ApiExplorerFeedbackContextProvider>
  );
};

export { ApiExplorerFeedbackContainer };
